// ExclusionRecoveryPanel — 荒らし復旧 第 2 段階「人を外して戻す」
// (vandalism-recovery-ui-2026-09-27.md §3「詳細設計 (2026-10-01 /consult で合意)」)。
//
// `RecoveryGenerationPickerModal` の「人を外して戻す」モードの本体。**サーバが
// `exclusionAvailable: true` と返したときだけ**マウントされる (表示の出し分けは
// 呼び出し元の 1 箇所だけ — この部品自体は条件を見ない)。
//
// 流れ:
//   1. 対象文書 (現行 / 過去キャンバス行) の絵ログを**新しい側から必要なぶんだけ**読む
//      (owner 限定 `/stroke-log` + 現行文書なら live tail)。範囲の決め方は
//      `application/exclusionWindow.ts`: 最後の描画から 2 時間 (印の有無で変えない)。
//      途中から切れている接続にチェックが入ったら自動で読み足す。「もっと前を読む」で広げられる。系譜 (`collectFinalDocumentRanges`) に絞って接続 (uid) ごとに集計し、
//      検知条件に当たった接続に印 + 初期チェック。
//   2. 接続一覧 (チェック = 外す) / 展開して 1 本ずつ (sid) 外す。
//   3. チェックが変わるたびに、確定時にサーバが土台にする世代 G の PNG から G 以降だけ
//      replay して「外したあと」を右に出す (debounce + 直列化 + 進捗)。G を決められない /
//      PNG を使えないときだけ、根まで読み足して全部 replay する。左は「今の絵」(外す線の
//      軌跡を重ねられる)。
//   4. 二段ボタン → サーバが外した版を焼く job → phase 表示 → 完了したら呼び出し元が
//      既存の `whiteboard.restore-request` を送る (復元後の「元に戻す」トーストは
//      既存経路がそのまま出す)。
//
// engine / drawing には触れない (replay は infrastructure/timelapse の既存部品)。

import type { PlanEvent, StrokeLogEvent } from '@oekaki/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type ExclusionBasePlan,
  type ExclusionBaseVerdict,
  planExclusion,
  toPlanEvents,
} from '../application/exclusionBase.js';
import {
  aggregateConnections,
  buildExclusionPayload,
  type ConnectionStat,
  type ConnectionSummary,
  type ExclusionSelection,
  exclusionPhaseLabel,
  filterExcludedEvents,
  findRangeGaps,
  formatAmount,
  formatStay,
  hasExclusion,
  markedUids,
  normalizeEndSeq,
  type StrokeStat,
} from '../application/exclusionCandidates.js';
import { runExclusionBake } from '../application/exclusionFlow.js';
import {
  decideLowerNeeds,
  extendNeed,
  findPossiblyCutUids,
  firstNeededCut,
  windowStartTs,
} from '../application/exclusionWindow.js';
import type { Transport } from '../application/ports/Transport.js';
import type { RecoveryPickerRow } from '../application/recoveryGenerations.js';
import { requestStrokeLogTailResult } from '../application/strokeLogTailRequest.js';
import { formatTimelapseTimeLabel } from '../application/timelapseShare.js';
import { readEnv } from '../infrastructure/env.js';
import { loadExclusionBaseImage } from '../infrastructure/pastCanvas/exclusionBaseImage.js';
import {
  createExclusionBasisSource,
  type ExclusionBasis,
  type ExclusionBasisPrefetch,
  type ExclusionBasisSource,
  prefetchExclusionBasis,
} from '../infrastructure/pastCanvas/exclusionBasisLoader.js';
import { fetchPastCanvasBackground } from '../infrastructure/pastCanvas/pastCanvasBackground.js';
import {
  type ExclusionPreviewResult,
  renderExclusionPreview,
} from '../infrastructure/timelapse/exclusionPreview.js';
import {
  type ExclusionPreviewBase,
  renderExclusionPreviewFromBase,
} from '../infrastructure/timelapse/exclusionPreviewFromBase.js';
import {
  createImagepasteResolver,
  type ImagepasteResolver,
} from '../infrastructure/timelapse/imagepasteResolve.js';
import { collectFinalDocumentRanges } from '../infrastructure/timelapse/lineageSeed.js';
import { buildLiveTailSegment } from '../infrastructure/timelapse/liveTail.js';
import { type SeqRange, trimEventsToRanges } from '../infrastructure/timelapse/segmentMerge.js';
import {
  type LowerNeed,
  openStrokeLogWindow,
  type StrokeLogWindow,
  type StrokeLogWindowState,
} from '../infrastructure/timelapse/strokeLogWindow.js';

/** 比較プレビューの長辺 (px)。取り違え対策で第 1 段階と同じ大きさ。 */
const PREVIEW_MAX_EDGE = 640;
/** チェック変更から replay を始めるまでの待ち (連続操作中は回さない)。 */
const PREVIEW_DEBOUNCE_MS = 400;
/** 展開したストローク一覧の初期表示件数。 */
const STROKE_PAGE = 30;
/** 手元に持つ土台 (G) の画素の数。 */
const BASE_CACHE_MAX = 2;
/** 読み足しループの暴走防止 (1 run あたりの最大ラウンド数)。 */
const PUMP_MAX_ROUNDS = 500;

const TAIL_FAILED_WARNING =
  '直近の描画 (まだ保存されていない分) を読み込めませんでした。最新の荒らしが一覧に出ていない可能性があります。「再読み込み」で試し直せます';

const LOAD_ERROR_MESSAGES = {
  'not-found': 'この部屋の絵ログが見つかりませんでした',
  unavailable: '絵ログを一時的に読み込めません。しばらくしてからもう一度お試しください',
  error: '絵ログの取得に失敗しました。通信環境を確認して再度お試しください',
} as const;

interface LoadedLog {
  /** 系譜の範囲に絞った (外す前の) イベント列。集計・オーバーレイ・資材の元。 */
  readonly scoped: readonly StrokeLogEvent[];
  /** 読み込んだ絵ログ (窓の中。系譜の根まで届いているとは限らない — `truncated`)。 */
  readonly all: readonly StrokeLogEvent[];
  /** プレビュー初期背景 (終端の meta)。null = 取れなかった (ログから導出に倒す)。 */
  readonly background: { readonly rgb: number; readonly alpha: number } | null;
  readonly ranges: readonly SeqRange[];
  readonly summary: ConnectionSummary;
  readonly warnings: readonly string[];
  /** G 判定用の軽量版イベント (全イベント、`all` と同順)。 */
  readonly planEvents: readonly PlanEvent[];
  /** G 判定の入力 (窓から解いた系譜 + 世代一覧)。null = 取れなかった (G は出さない)。 */
  readonly basis: ExclusionBasis | null;
  /** 系譜の根まで読めていない (読んだ範囲より前にも絵ログがある)。 */
  readonly truncated: boolean;
  /** 読んだ範囲の先頭の時刻 (ms)。 */
  readonly startTs: number | null;
  /** 範囲の下端で切れている可能性のある接続。 */
  readonly cutUids: ReadonlySet<string>;
}

/** 開いた絵ログの窓と、読み足しループが使うもの。 */
interface WindowCtl {
  readonly window: StrokeLogWindow;
  readonly basisSource: ExclusionBasisSource;
  readonly tailFailed: boolean;
  readonly background: { readonly rgb: number; readonly alpha: number } | null;
  readonly rowEndSeq: number | undefined;
  readonly signal: AbortSignal;
  /** 直近に組み立てた log (窓の状態が変わらなければ作り直さない)。 */
  cache: { readonly key: string; readonly log: LoadedLog } | null;
}

type FallbackReason = 'no-plan' | 'base-image';

type LoadState =
  | { readonly t: 'loading' }
  | { readonly t: 'error'; readonly message: string }
  | { readonly t: 'empty' }
  | { readonly t: 'ready'; readonly log: LoadedLog };

type PreviewState =
  | { readonly t: 'pending' }
  /** 外す対象が無いので出すものが無い。 */
  | { readonly t: 'idle' }
  | { readonly t: 'running'; readonly fraction: number; readonly stage: 'loading-base' | 'replay' }
  | { readonly t: 'ready'; readonly skipped: number; readonly total: number }
  | { readonly t: 'failed' };

type JobState =
  | { readonly t: 'idle' }
  | { readonly t: 'running'; readonly phase: string }
  | { readonly t: 'error'; readonly message: string }
  | { readonly t: 'skipped-warning'; readonly pastCanvasId: string; readonly skipped: number };

export interface ExclusionRecoveryPanelProps {
  readonly roomId: string;
  readonly transport: Transport;
  readonly adminToken?: string;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** 行から開いた場合その行 (= 対象文書)。省略 = 現行文書。 */
  readonly row?: RecoveryPickerRow;
  /**
   * 現行文書の現在の背景色 (rgb24、手元のキャンバスが持っている色)。プレビューの
   * 初期背景に使う。省略 = 取れない (ログから導出に倒す)。過去キャンバス行では
   * 行の snapshot meta から取るので使わない。
   */
  readonly currentBackgroundRgb?: number;
  /** 「今の絵」のプレビュー (object URL)。まだ無ければ null。 */
  readonly currentPreviewUrl: string | null;
  /** 復元要求の送信中 (job 完了後 〜 `whiteboard.restore` 待ち)。 */
  readonly restoring: boolean;
  /** job が完了したら呼ぶ (呼び出し元が `restore-request` を送る)。 */
  readonly onBaked: (pastCanvasId: string) => void;
  readonly onCancel: () => void;
}

/**
 * G (plan.decisions) を決められないとき、根まで読んで全 replay に倒すか。
 * `nothing_to_exclude` は窓を広げても変わらないので倒さない (`no_base_generation` や plan なしは倒す)。
 */
export const shouldFallbackToFull = (input: {
  readonly fallbackActive: boolean;
  readonly sel: ExclusionSelection;
  readonly plan: ExclusionBasePlan | null;
}): boolean =>
  !input.fallbackActive &&
  hasExclusion(input.sel) &&
  input.plan?.decisions == null &&
  input.plan?.verdict.kind !== 'nothing_to_exclude';

const exclusionBaseMessage = (v: ExclusionBaseVerdict): string => {
  switch (v.kind) {
    case 'base': {
      const at = v.base.savedAt === null ? NaN : Date.parse(v.base.savedAt);
      return Number.isNaN(at)
        ? '戻すときは、外す人が描く前に保存された絵から作り直します'
        : `戻すときは、${formatTimelapseTimeLabel(at)} に保存された絵から作り直します (それより後の絵は、外す人を除いて描き直します)`;
    }
    case 'blank':
      return '戻すときは、白紙から全部描き直します (外す人より前に保存された絵がないため)';
    case 'nothing_to_exclude':
      return '選んだ人・線はこの文書の絵に残っていないため、外して戻すことはできません';
    case 'no_base_generation':
      return '外す人より前に保存された絵が見つからないため、この人を外して戻すことはできません。別の人・線を選ぶか、過去の時点を選んで戻してください';
  }
};

const penLabel = (s: StrokeStat): string => (s.isEraser ? '消しゴム' : '線');

const nameOf = (c: ConnectionStat): string =>
  c.displayNames.length > 0 ? c.displayNames.join(' → ') : '(名前なし)';

/** この選択での G の判定 (サーバと同じ規則・同じ入力)。入力が取れていない / 外す対象が無ければ null。 */
const planFor = (log: LoadedLog, sel: ExclusionSelection): ExclusionBasePlan | null => {
  if (log.basis === null || !hasExclusion(sel)) return null;
  const p = buildExclusionPayload(log.summary, sel);
  return planExclusion({
    planEvents: log.planEvents,
    docs: log.basis.docs,
    excludeUids: p.excludeUids,
    excludeSids: p.excludeSids,
    generations: log.basis.generations,
  });
};

/** 窓の今の状態から画面用の log を組み立てる (範囲は「読んだ範囲」で集計する)。 */
const buildLoadedLog = async (st: StrokeLogWindowState, ctl: WindowCtl): Promise<LoadedLog> => {
  const all = st.events;
  const lastSeq = all[all.length - 1]?.seq ?? 0;
  const endSeq = ctl.rowEndSeq === undefined ? lastSeq : normalizeEndSeq(all, ctl.rowEndSeq);
  const { basis, truncated } = await ctl.basisSource.resolve({
    events: all,
    hasOlder: st.hasOlder,
    floorSeq: st.floorSeq,
  });
  const ranges = collectFinalDocumentRanges(all, endSeq, ctl.basisSource.bakedSessionIds);
  const scoped = trimEventsToRanges(all, ranges);
  const summary = aggregateConnections(scoped);
  const warnings: string[] = [];
  // 過去キャンバス行は live tail を使わない (終端が決まっている) ので警告しない。
  if (ctl.tailFailed) warnings.push(TAIL_FAILED_WARNING);
  if (findRangeGaps(st.coverage, ranges).length > 0) {
    warnings.push(
      '絵ログに抜けがあります。プレビューが実際と違う可能性があり、サーバーが戻せないと判断することがあります',
    );
  }
  if (st.skippedSegments.length > 0) {
    warnings.push(
      `絵ログの一部 (${st.skippedSegments.length} 件) を読み込めませんでした。プレビューが不完全です`,
    );
  }
  return {
    scoped,
    all,
    background: ctl.background,
    ranges,
    summary,
    warnings,
    planEvents: toPlanEvents(all),
    // 読み込めない絵ログがあると系譜が不完全なので G は出さない。
    basis: st.skippedSegments.length > 0 ? null : basis,
    truncated,
    startTs: windowStartTs(all),
    cutUids: findPossiblyCutUids({ summary, scoped, truncated, floorToSeq: st.floorToSeq }),
  };
};

interface ReadPumpHandles {
  readonly uids: { current: ReadonlySet<string> };
  readonly sids: { current: ReadonlySet<string> };
  readonly userNeeds: { current: LowerNeed[] };
  readonly knownMarked: { current: Set<string> };
  readonly fullRequested: { current: boolean };
  readonly setUids: (f: (prev: ReadonlySet<string>) => ReadonlySet<string>) => void;
  readonly setLoad: (f: (prev: LoadState) => LoadState) => void;
  readonly setSettling: (reason: string | null) => void;
}

const READING_FIRST = '外す人を探して、新しい側から絵ログを読み込んでいます…';
const READING_ALL = '全部の絵ログを読み込んでいます (時間がかかる方法でプレビューするため)…';
const READING_EARLIER = 'さらに前の絵ログを読み込んでいます…';
const READING_BASE = '外す人より前の保存点まで、絵ログを読み足しています…';

/**
 * 必要な範囲が読めるまで、窓を古い側へ 1 ラウンドずつ読み足す。毎ラウンド集計・印・切れた
 * 接続・G を作り直して画面へ渡す (読み足している間 `setSettling` で確定・プレビューを待たせる)。
 */
const runReadPump = async (ctl: WindowCtl, run: AbortSignal, h: ReadPumpHandles): Promise<void> => {
  const stopped = (): boolean => run.aborted || ctl.signal.aborted;
  for (let round = 0; round < PUMP_MAX_ROUNDS; round++) {
    if (stopped()) return;
    const st = ctl.window.state();
    if (st.events.length === 0) {
      // 新しい側が空 (イベントの無いセグメントだけ): 古い側に残っていれば読み足す。
      if (st.hasOlder) {
        h.setSettling(READING_FIRST);
        if (await ctl.window.step({ kind: 'more' })) continue;
      }
      h.setSettling(null);
      h.setLoad(() => ({ t: 'empty' }));
      return;
    }
    const key = `${st.fetchedCount}:${st.events.length}:${st.skippedSegments.length}`;
    let log: LoadedLog;
    if (ctl.cache !== null && ctl.cache.key === key) {
      log = ctl.cache.log;
    } else {
      log = await buildLoadedLog(st, ctl);
      if (stopped()) return;
      ctl.cache = { key, log };
    }
    // 新しく印が付いた接続 (読み足して見えた分も) は最初から「外す」にチェックする。
    const newlyMarked = markedUids(log.summary).filter((u) => !h.knownMarked.current.has(u));
    for (const u of newlyMarked) h.knownMarked.current.add(u);
    const checked = new Set([...h.uids.current, ...newlyMarked]);
    if (newlyMarked.length > 0) {
      h.uids.current = checked;
      h.setUids((prev) => new Set([...prev, ...newlyMarked]));
    }
    h.setLoad((prev) => (prev.t === 'ready' && prev.log === log ? prev : { t: 'ready', log }));

    const sel = { uids: checked, sids: h.sids.current };
    const plan = planFor(log, sel);
    const needs = decideLowerNeeds({
      full: h.fullRequested.current,
      userNeeds: h.userNeeds.current,
      summary: log.summary,
      lastEventTs: st.events.reduce<number | null>(
        (m, e) => (m === null || e.ts > m ? e.ts : m),
        null,
      ),
      truncated: log.truncated,
      cutUids: log.cutUids,
      checkedUids: checked,
      noBaseGeneration: plan?.verdict.kind === 'no_base_generation',
    });
    const pending = needs.find((n) => !ctl.window.isCovered(n));
    if (pending === undefined) {
      h.setSettling(null);
      return;
    }
    let reason: string;
    if (pending.kind === 'all') reason = READING_ALL;
    else if (h.userNeeds.current.includes(pending)) reason = READING_EARLIER;
    else if (pending.kind === 'more') {
      const name = firstNeededCut({
        summary: log.summary,
        cutUids: log.cutUids,
        checkedUids: checked,
      });
      reason =
        name !== null
          ? `「${name}」が範囲の途中から始まっていたので、その人の最初まで読み足しています…`
          : READING_BASE;
    } else reason = READING_FIRST;
    h.setSettling(reason);
    if (!(await ctl.window.step(pending))) {
      // これ以上読めない (読み切った / 中断): 今ある範囲で止める。
      h.setSettling(null);
      return;
    }
  }
  h.setSettling(null);
};

export const ExclusionRecoveryPanel = ({
  roomId,
  transport,
  adminToken,
  canvasWidth,
  canvasHeight,
  row,
  currentBackgroundRgb,
  currentPreviewUrl,
  restoring,
  onBaked,
  onCancel,
}: ExclusionRecoveryPanelProps): React.ReactNode => {
  const [load, setLoad] = useState<LoadState>({ t: 'loading' });
  const [uids, setUids] = useState<ReadonlySet<string>>(new Set());
  const [sids, setSids] = useState<ReadonlySet<string>>(new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [strokeLimit, setStrokeLimit] = useState<ReadonlyMap<string, number>>(new Map());
  const [onlyMarked, setOnlyMarked] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [armed, setArmed] = useState(false);
  const [job, setJob] = useState<JobState>({ t: 'idle' });
  const [preview, setPreview] = useState<PreviewState>({ t: 'pending' });
  // 絵ログ読み込みのやり直し用。増やすと load effect が走り直す (チェックは初期状態に戻る)。
  const [reloadKey, setReloadKey] = useState(0);
  // 絵ログの窓 (新しい側から必要なぶんだけ読み足す)。null = まだ開けていない。
  const [ctl, setCtl] = useState<WindowCtl | null>(null);
  // 読み足し中の理由。null = 落ち着いている (確定・プレビューはこの間は待つ)。
  const [settling, setSettling] = useState<string | null>(null);
  // 「もっと前を読む」/ fallback で読み足しループを起こし直す合図。
  const [pumpTick, setPumpTick] = useState(0);
  // 土台 (G) を使えず、根から全部 replay している選択 (選択が変わったら G を試し直す)。
  const [fallback, setFallback] = useState<{
    readonly sel: ExclusionSelection;
    readonly reason: FallbackReason;
  } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const selection = useMemo(() => ({ uids, sids }), [uids, sids]);
  const bakeAbortRef = useRef<AbortController | null>(null);
  const uidsRef = useRef(uids);
  uidsRef.current = uids;
  const sidsRef = useRef(sids);
  sidsRef.current = sids;
  const settlingRef = useRef(settling);
  settlingRef.current = settling;
  // 読み足しの状態 (再描画に関係しない)。
  const userNeedsRef = useRef<LowerNeed[]>([]);
  const knownMarkedRef = useRef<Set<string>>(new Set());
  const fullRequestedRef = useRef(false);
  const pumpChainRef = useRef<Promise<void>>(Promise.resolve());
  // 土台 (G) の画素は (sessionId, seq) で最大 2 つまで持つ (1 つ 2000² × レイヤ数で大きい)。
  const baseCacheRef = useRef<Map<string, ExclusionPreviewBase>>(new Map());
  const resolverMapRef = useRef<WeakMap<LoadedLog, ImagepasteResolver>>(new WeakMap());

  // row の primitive だけ依存に使う (親が毎回 object を作り直しても再 fetch しない)。
  const rowPastCanvasId = row?.pastCanvasId;
  const currentBackgroundRef = useRef(currentBackgroundRgb);
  currentBackgroundRef.current = currentBackgroundRgb;
  const rowEndSeq = row !== undefined ? Math.max(row.clearedSeq ?? 0, row.snapshotSeq) : undefined;

  // ── 絵ログの窓を開く (新しい 4 本 + 現行文書なら live tail) ─────────────────────
  useEffect(() => {
    void reloadKey; // 再読み込みボタンで増える (依存に含めて effect を走り直す)
    const ac = new AbortController();
    setLoad({ t: 'loading' });
    setCtl(null);
    setSettling(null);
    setFallback(null);
    setUids(new Set());
    setSids(new Set());
    userNeedsRef.current = [];
    knownMarkedRef.current = new Set();
    fullRequestedRef.current = false;
    baseCacheRef.current = new Map();
    void (async () => {
      let tailFailed = false;
      // 新しい側から必要なぶんだけ読む (範囲の決め方は application/exclusionWindow.ts)。
      // 現行文書だけ未 flush の直近分 (live tail) を足す。過去キャンバス行は終端が決まっているので不要。
      const basisPrefetch = prefetchExclusionBasis({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        pastCanvasId: rowPastCanvasId,
        ...(adminToken !== undefined ? { adminToken } : {}),
        signal: ac.signal,
      }).catch((): ExclusionBasisPrefetch => ({ list: { outcome: 'error' }, current: null }));
      const [opened, rowBackground] = await Promise.all([
        openStrokeLogWindow({
          dispatcherUrl: readEnv().dispatcherUrl,
          roomId,
          signal: ac.signal,
          ...(adminToken !== undefined ? { adminToken } : {}),
          ...(rowEndSeq !== undefined
            ? { endSeq: rowEndSeq }
            : {
                liveTail: async (sinceSeq: number) => {
                  const res = await requestStrokeLogTailResult(transport, sinceSeq, {
                    signal: ac.signal,
                  });
                  if (!res.ok) {
                    // 中断 (画面を閉じた / 再読み込み) は失敗ではない。
                    if (res.reason !== 'aborted') tailFailed = true;
                    return null;
                  }
                  return buildLiveTailSegment(roomId, res.tail);
                },
              }),
        }),
        rowPastCanvasId !== undefined
          ? fetchPastCanvasBackground({
              dispatcherUrl: readEnv().dispatcherUrl,
              roomId,
              pastCanvasId: rowPastCanvasId,
              ...(adminToken !== undefined ? { adminToken } : {}),
              signal: ac.signal,
            })
          : Promise.resolve(null),
      ]);
      if (ac.signal.aborted) return;
      if (opened.outcome !== 'ok') {
        setLoad({
          t: 'error',
          message: LOAD_ERROR_MESSAGES[opened.outcome === 'error' ? 'error' : opened.outcome],
        });
        return;
      }
      const prefetch = await basisPrefetch;
      if (ac.signal.aborted) return;
      const background =
        rowPastCanvasId !== undefined
          ? rowBackground
          : currentBackgroundRef.current !== undefined
            ? { rgb: currentBackgroundRef.current, alpha: 255 }
            : null;
      setCtl({
        window: opened.window,
        basisSource: createExclusionBasisSource({
          dispatcherUrl: readEnv().dispatcherUrl,
          roomId,
          pastCanvasId: rowPastCanvasId,
          rowEndSeq,
          prefetch,
          ...(adminToken !== undefined ? { adminToken } : {}),
          signal: ac.signal,
        }),
        tailFailed: rowEndSeq === undefined && tailFailed,
        background,
        rowEndSeq,
        signal: ac.signal,
        cache: null,
      });
    })();
    return () => ac.abort();
  }, [roomId, adminToken, transport, rowEndSeq, rowPastCanvasId, reloadKey]);

  // ── 読み足しループ: 必要な範囲が読めるまで古い側へ 1 ラウンドずつ読み足し、集計・G をやり直す ──
  // 走らせるのは窓を開いた直後・チェックが変わったとき・「もっと前を読む」・fallback のとき。
  // 同時に 2 本は走らせない (前の run が終わってから次を始める)。
  useEffect(() => {
    void pumpTick;
    void fallback;
    void uids; // チェックが変わったら切れた接続の読み足しを判定し直す
    if (ctl === null) return;
    const run = new AbortController();
    const prev = pumpChainRef.current;
    pumpChainRef.current = prev
      .then(() =>
        runReadPump(ctl, run.signal, {
          uids: uidsRef,
          sids: sidsRef,
          userNeeds: userNeedsRef,
          knownMarked: knownMarkedRef,
          fullRequested: fullRequestedRef,
          setUids,
          setLoad,
          setSettling,
        }),
      )
      .catch(() => {
        if (!run.signal.aborted) {
          setSettling(null);
          setLoad({ t: 'error', message: LOAD_ERROR_MESSAGES.error });
        }
      });
    return () => run.abort();
  }, [ctl, uids, pumpTick, fallback]);

  const log = load.t === 'ready' ? load.log : null;

  const payload = useMemo(
    () => (log !== null ? buildExclusionPayload(log.summary, selection) : null),
    [log, selection],
  );

  // 確定時にサーバが土台にする世代 G (サーバと同じ規則・同じ入力) と、そのイベントごとの扱い。
  // 外す対象が無い間、または入力が取れなかったときは null (何も出さない。確定の可否はサーバが正)。
  const plan = useMemo<ExclusionBasePlan | null>(
    () => (log !== null && hasExclusion(selection) ? planFor(log, selection) : null),
    [log, selection],
  );
  const verdict = plan?.verdict ?? null;
  const verdictBlocks =
    verdict?.kind === 'nothing_to_exclude' || verdict?.kind === 'no_base_generation';
  const fallbackReason = fallback !== null && fallback.sel === selection ? fallback.reason : null;

  // ── プレビュー: 選択が変わったら今の replay を中断し、debounce 後に最新の選択でやり直す ──
  // 土台 (G) の PNG から G 以降だけ replay する。G を決められない / PNG を使えないときは
  // 根から全部 replay する (そのために絵ログを根まで読み足す)。
  // 古い run の結果は canvas にも状態にも反映しない (`ac.signal.aborted` で判定)。
  useEffect(() => {
    if (log === null) return;
    const sel = selection;
    // 選択が変わった: 二段ボタンを戻し、プレビューを「更新待ち」にする。
    setPreview({ t: 'pending' });
    // 読み足し中、または全 replay のために根まで読み足している間は待つ。
    if (settling !== null || (fallbackReason !== null && log.truncated)) return;
    // G を決められない (世代一覧が取れない / 確定が通らない条件): 根まで読んで全 replay に倒す。
    // ただし `nothing_to_exclude` は全部読んでも結果が変わらない (外す対象が系譜に無い) ので倒さない。
    if (shouldFallbackToFull({ fallbackActive: fallbackReason !== null, sel, plan })) {
      fullRequestedRef.current = true;
      setFallback({ sel, reason: 'no-plan' });
      return;
    }
    const ac = new AbortController();
    const run = async (): Promise<void> => {
      if (settlingRef.current !== null) return;
      if (!hasExclusion(sel)) {
        setPreview({ t: 'idle' });
        return;
      }
      const canvasSize = { width: canvasWidth, height: canvasHeight };
      const onProgress = (fraction: number): void => {
        if (!ac.signal.aborted) setPreview({ t: 'running', fraction, stage: 'replay' });
      };
      try {
        let frame: ExclusionPreviewResult | null;
        if (fallbackReason === null) {
          const decisions = plan?.decisions ?? null;
          if (decisions === null) {
            // nothing_to_exclude: 出すプレビューが無い (理由は verdict の文言で出る)。
            setPreview(
              plan?.verdict.kind === 'nothing_to_exclude' ? { t: 'idle' } : { t: 'failed' },
            ); // 上の fallback 判定で弾いてあるので通常は来ない
            return;
          }
          let base: ExclusionPreviewBase | null = null;
          if (decisions.base !== null) {
            setPreview({ t: 'running', fraction: 0, stage: 'loading-base' });
            base = await loadBase(decisions.base.sessionId, decisions.base.seq, ac.signal);
            if (ac.signal.aborted) return;
            if (base === null) {
              fullRequestedRef.current = true;
              setFallback({ sel, reason: 'base-image' });
              return;
            }
          }
          setPreview({ t: 'running', fraction: 0, stage: 'replay' });
          frame = await renderExclusionPreviewFromBase({
            canvas: canvasSize,
            base,
            decisions,
            events: log.all,
            maxEdge: PREVIEW_MAX_EDGE,
            ...(log.background !== null ? { background: log.background } : {}),
            resolveImagepaste: resolverOf(log).resolve,
            onProgress,
            signal: ac.signal,
          });
        } else {
          setPreview({ t: 'running', fraction: 0, stage: 'replay' });
          frame = await renderExclusionPreview({
            events: filterExcludedEvents(log.all, sel),
            canvas: canvasSize,
            seqRanges: log.ranges,
            ...(log.background !== null ? { background: log.background } : {}),
            maxEdge: PREVIEW_MAX_EDGE,
            resolveImagepaste: resolverOf(log).resolve,
            onProgress,
            signal: ac.signal,
          });
        }
        if (ac.signal.aborted) return;
        if (frame === null) {
          setPreview({ t: 'failed' });
          return;
        }
        const cv = canvasRef.current;
        const ctx = cv?.getContext('2d') ?? null;
        if (cv !== null && ctx !== null) {
          cv.width = frame.width;
          cv.height = frame.height;
          ctx.putImageData(new ImageData(frame.rgba, frame.width, frame.height), 0, 0);
        }
        setPreview({
          t: 'ready',
          skipped: frame.skippedImagepastes,
          total: frame.totalImagepastes,
        });
      } catch {
        if (!ac.signal.aborted) setPreview({ t: 'failed' });
      }
    };
    const loadBase = async (
      sessionId: string,
      seq: number,
      signal: AbortSignal,
    ): Promise<ExclusionPreviewBase | null> => {
      const cache = baseCacheRef.current;
      const key = `${sessionId}:${seq}`;
      const hit = cache.get(key);
      if (hit !== undefined) return hit;
      const base = await loadExclusionBaseImage({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        sessionId,
        seq,
        canvas: { width: canvasWidth, height: canvasHeight },
        signal,
        ...(adminToken !== undefined ? { adminToken } : {}),
      });
      if (base !== null) {
        cache.set(key, base);
        while (cache.size > BASE_CACHE_MAX) {
          const oldest = cache.keys().next().value;
          if (oldest === undefined) break;
          cache.delete(oldest);
        }
      }
      return base;
    };
    // 貼り付け画像の取得は log ごとに 1 回だけ用意する (読み足し中の log では作らない)。
    const resolverOf = (target: LoadedLog): ImagepasteResolver => {
      const map = resolverMapRef.current;
      let r = map.get(target);
      if (r === undefined) {
        r = createImagepasteResolver({
          dispatcherUrl: readEnv().dispatcherUrl,
          roomId,
          ...(adminToken !== undefined ? { adminToken } : {}),
          events: target.scoped,
        });
        map.set(target, r);
      }
      return r;
    };
    const timer = setTimeout(() => {
      void run();
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [
    log,
    selection,
    plan,
    settling,
    fallbackReason,
    canvasWidth,
    canvasHeight,
    roomId,
    adminToken,
  ]);

  // unmount: job の待ちを中断 (replay は上の effect の cleanup が中断する)。
  useEffect(
    () => () => {
      bakeAbortRef.current?.abort();
    },
    [],
  );

  // ── 操作 ───────────────────────────────────────────────────────────────────
  const resetConfirm = useCallback((): void => {
    setArmed(false);
    setJob((j) => (j.t === 'error' ? { t: 'idle' } : j));
  }, []);

  const toggleUid = (uid: string): void => {
    setUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
    resetConfirm();
  };
  const toggleSid = (sid: string): void => {
    setSids((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
    resetConfirm();
  };
  const toggleExpanded = (uid: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const marked = useMemo(() => (log !== null ? markedUids(log.summary) : []), [log]);
  const allMarkedChecked = marked.length > 0 && marked.every((u) => uids.has(u));
  const toggleMarked = (): void => {
    setUids((prev) => {
      const next = new Set(prev);
      if (marked.every((u) => next.has(u))) for (const u of marked) next.delete(u);
      else for (const u of marked) next.add(u);
      return next;
    });
    resetConfirm();
  };

  // 「今の絵」に重ねる軌跡: 外す対象のストローク (uid を外す接続は全部、+ 個別 sid)。
  const overlayStrokes = useMemo<readonly StrokeStat[]>(() => {
    if (log === null || !showOverlay) return [];
    const out: StrokeStat[] = [];
    for (const c of log.summary.connections) {
      for (const s of c.strokes) if (uids.has(c.uid) || sids.has(s.sid)) out.push(s);
    }
    return out;
  }, [log, uids, sids, showOverlay]);

  // 「もっと前を読む」: いま読んでいる範囲の先頭からさらに 2 時間ぶん前まで (取れなければ読める所まで)。
  // 単位を「時間」にしたのは、セグメントの本数だと絵ログの密度で実際に読む範囲が大きく変わるため。
  const readEarlier = (): void => {
    if (log === null || log.startTs === null) return;
    userNeedsRef.current = [...userNeedsRef.current, extendNeed(log.startTs)];
    setPumpTick((t) => t + 1);
  };

  const startBake = (target: LoadedLog | null): void => {
    if (target === null || payload === null) return;
    const ac = new AbortController();
    bakeAbortRef.current = ac;
    setArmed(false);
    setJob({ t: 'running', phase: 'queued' });
    void (async () => {
      const out = await runExclusionBake({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        ...(adminToken !== undefined ? { adminToken } : {}),
        pastCanvasId: rowPastCanvasId ?? null,
        excludeUids: payload.excludeUids,
        excludeSids: payload.excludeSids,
        onPhase: (phase) => setJob({ t: 'running', phase }),
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      if (out.outcome === 'failed') {
        setJob({ t: 'error', message: out.message });
        return;
      }
      if (out.stats.skippedImagepastes > 0) {
        // 貼り付け画像が再現できなかった: 戻す前に必ず見せる (戻すと画面が閉じて
        // 警告が消えるため)。焼いた行は過去キャンバスに残るので「やめる」でも無駄にならない。
        setJob({
          t: 'skipped-warning',
          pastCanvasId: out.pastCanvasId,
          skipped: out.stats.skippedImagepastes,
        });
        return;
      }
      setJob({ t: 'idle' });
      onBaked(out.pastCanvasId);
    })();
  };

  const busy = job.t === 'running' || restoring;
  const previewSettled = preview.t === 'ready';
  // 絵ログを読み足している間は G も一覧も途中なので確定させない。
  const canArm =
    log !== null &&
    hasExclusion(selection) &&
    previewSettled &&
    !busy &&
    !verdictBlocks &&
    settling === null;

  // ── 描画 ───────────────────────────────────────────────────────────────────
  if (load.t === 'loading') {
    return (
      <p role="status" className="text-sm text-[var(--color-text-muted)]">
        絵ログを読み込んでいます…
      </p>
    );
  }
  if (load.t === 'error') {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm text-[var(--color-text)]">{load.message}</p>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-bg-soft)]"
        >
          再読み込み
        </button>
      </div>
    );
  }
  if (load.t === 'empty' || log === null) {
    return (
      <p className="text-sm text-[var(--color-text)]">
        この文書には絵ログがないため、人を外して戻すことはできません。
      </p>
    );
  }

  const visible = log.summary.connections.filter((c) => !onlyMarked || c.marks.length > 0);
  const excludedCount = payload?.excludeUids.length ?? 0;
  const excludedSidCount = payload?.excludeSids.length ?? 0;
  const summaryLabel = `${excludedCount}人${excludedSidCount > 0 ? `・${excludedSidCount}本` : ''}を外す`;

  const previewBox = (label: string, body: React.ReactNode): React.ReactNode => (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="select-none text-[11px] text-[var(--color-text-muted)]">{label}</span>
      <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-bg)]">
        {body}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[var(--color-text-muted)] text-xs">
        外す人・線にチェックを入れると、右の絵がその場で作り直されます。荒らしの跡を片付けようとして消しゴムで消した人も、外すと元の絵が戻ります。
        入り直しが続いている間に戻すとまた荒らされるので、波が収まってから 1
        回だけ戻すのがおすすめです。
      </p>

      {log.warnings.map((w) => (
        <p key={w} role="alert" className="text-[var(--color-error)] text-xs">
          {w}
        </p>
      ))}

      {marked.length > 0 && (
        <p
          data-testid="exclusion-auto-checked-note"
          role="status"
          className="rounded border border-[var(--color-error)] p-2 text-[var(--color-text)] text-xs"
        >
          「荒らしの疑い」の印が付いた {marked.length}{' '}
          人は、最初から「外す」にチェックを入れてあります
          (一覧の赤い印の人)。右の絵は、チェックした人を外したあとの絵で、最新の絵そのものではありません。
        </p>
      )}

      {/* 比較プレビュー: デスクトップ横並び / スマホ縦並び。 */}
      <div className="flex flex-col gap-3 sm:flex-row">
        {previewBox(
          '今の絵',
          <>
            {currentPreviewUrl !== null ? (
              <img
                src={currentPreviewUrl}
                alt="今の絵"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span className="text-[11px] text-[var(--color-text-muted)]">プレビューなし</span>
            )}
            {overlayStrokes.length > 0 && (
              <svg
                data-testid="exclusion-overlay"
                aria-hidden="true"
                viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
                preserveAspectRatio="xMidYMid meet"
                className="pointer-events-none absolute inset-0 h-full w-full"
              >
                {overlayStrokes.map((s) => (
                  <polyline
                    key={s.sid}
                    points={s.path.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill="none"
                    stroke="rgb(255 0 80)"
                    strokeOpacity={0.55}
                    strokeWidth={Math.max(4, Math.min(s.width, 40))}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </svg>
            )}
          </>,
        )}
        {previewBox(
          '外したあとの絵 (チェックした人を外した状態)',
          <>
            <canvas
              ref={canvasRef}
              data-testid="exclusion-preview-canvas"
              className={`max-h-full max-w-full object-contain transition-opacity${
                preview.t === 'running' || preview.t === 'pending' ? ' opacity-40' : ''
              }${preview.t === 'idle' ? ' invisible' : ''}`}
            />
            {preview.t === 'idle' && (
              <span className="absolute inset-0 flex items-center justify-center p-2 text-center text-[11px] text-[var(--color-text-muted)]">
                外す人・線を選ぶと、外したあとの絵がここに出ます
              </span>
            )}
            {(preview.t === 'running' || preview.t === 'pending') && (
              <span
                role="status"
                aria-label="プレビューを更新中"
                className="absolute inset-0 flex flex-col items-center justify-center gap-1"
              >
                <span
                  aria-hidden="true"
                  className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
                />
                <span className="rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
                  {preview.t === 'running'
                    ? preview.stage === 'loading-base'
                      ? '土台の絵を読み込んでいます…'
                      : `再生しています… ${Math.round(preview.fraction * 100)}%`
                    : settling !== null
                      ? '絵ログを読み込み中です…'
                      : '更新を待っています…'}
                </span>
              </span>
            )}
            {preview.t === 'failed' && (
              <span role="alert" className="text-[11px] text-[var(--color-error)]">
                プレビューを作れませんでした
              </span>
            )}
          </>,
        )}
      </div>
      <label className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]">
        <input
          type="checkbox"
          checked={showOverlay}
          onChange={(e) => setShowOverlay(e.target.checked)}
        />
        外す線の軌跡を「今の絵」に重ねる
      </label>
      {fallbackReason !== null && (
        <p
          data-testid="exclusion-fallback-note"
          role="status"
          className="text-[11px] text-[var(--color-text-muted)]"
        >
          時間がかかる方法でプレビューしています (
          {fallbackReason === 'base-image'
            ? '土台の絵を読み込めなかったため、最初から描き直しています'
            : '土台の絵を決められないため、最初から描き直しています'}
          )
        </p>
      )}
      {preview.t === 'ready' && preview.skipped > 0 && (
        <p role="status" className="text-[#b8860b] text-xs">
          {preview.total} 件中 {preview.skipped} 件の貼り付け画像をプレビューに出せませんでした
          (実際の結果とは違う可能性があります)
        </p>
      )}

      {/* 接続一覧 */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-bold text-[var(--color-text)] text-sm">この文書で描いた人</h3>
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {log.summary.connections.length} 接続 (入り直すと別の行になります)
          </span>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            title="絵ログを読み込み直します。チェックは最初の状態に戻ります"
            className="rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-bg-soft)]"
          >
            再読み込み
          </button>
          {marked.length > 0 && (
            <button
              type="button"
              onClick={toggleMarked}
              className="rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-bg-soft)]"
            >
              {allMarkedChecked ? '印の付いた人を戻す' : '印の付いた人をまとめて外す'}
            </button>
          )}
          <label className="flex items-center gap-1 text-[11px] text-[var(--color-text-muted)]">
            <input
              type="checkbox"
              checked={onlyMarked}
              onChange={(e) => setOnlyMarked(e.target.checked)}
            />
            印の付いた人だけ表示
          </label>
        </div>
        <div
          data-testid="exclusion-range-note"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[var(--color-text-muted)]"
        >
          <span>
            {log.truncated && log.startTs !== null
              ? `${formatTimelapseTimeLabel(log.startTs)} 以降に描いた人を表示しています (それより前に描いた人は出ません)`
              : 'この文書の最初から読み込んでいます'}
          </span>
        </div>
        {settling !== null && (
          <p
            data-testid="exclusion-settling"
            role="status"
            className="flex items-center gap-2 text-[var(--color-text)] text-xs"
          >
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
            />
            {settling}
          </p>
        )}
        <ul className="flex max-h-[40vh] flex-col gap-1 overflow-y-auto rounded border border-[var(--color-border)] p-1">
          {visible.length === 0 && (
            <li className="p-2 text-[11px] text-[var(--color-text-muted)]">
              該当する接続がありません
            </li>
          )}
          {visible.map((c) => {
            const isOut = uids.has(c.uid);
            const isOpen = expanded.has(c.uid);
            const limit = strokeLimit.get(c.uid) ?? STROKE_PAGE;
            return (
              <li
                key={c.uid}
                data-testid={`connection-${c.uid}`}
                className={`rounded border p-1.5 ${
                  isOut
                    ? 'border-[var(--color-error)] bg-[var(--color-bg-soft)]'
                    : 'border-transparent'
                }`}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={isOut}
                      onChange={() => toggleUid(c.uid)}
                      aria-label={`${nameOf(c)} を外す`}
                    />
                    <span className="font-bold text-[var(--color-text)]">{nameOf(c)}</span>
                  </label>
                  {c.marks.length > 0 && (
                    <span
                      title={c.marks.map((m) => m.label).join(' / ')}
                      className="rounded bg-[var(--color-error)] px-1.5 py-0.5 font-bold text-[10px] text-white"
                    >
                      荒らしの疑い
                    </span>
                  )}
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    {formatTimelapseTimeLabel(c.firstTs)}〜{formatTimelapseTimeLabel(c.lastTs)} (
                    {formatStay(c.lastTs - c.firstTs)})
                  </span>
                  <span className="flex-1" />
                  <button
                    type="button"
                    onClick={() => toggleExpanded(c.uid)}
                    aria-expanded={isOpen}
                    aria-label={`${nameOf(c)} のストロークを${isOpen ? '閉じる' : '開く'}`}
                    className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[11px] hover:bg-[var(--color-bg)]"
                  >
                    {isOpen ? '▲ 閉じる' : '▼ 1本ずつ'}
                  </button>
                </div>
                <div className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">
                  {c.strokeCount}本 / 描いた量 {formatAmount(c.drawInk)} / 消した量{' '}
                  {formatAmount(c.eraseInk)} / 最大の太さ {c.maxWidth} / 最長{' '}
                  {Math.round(c.longestStroke)}px
                  {c.imagepasteCount > 0 ? ` / 貼り付け画像 ${c.imagepasteCount}枚` : ''}
                </div>
                {c.marks.length > 0 && (
                  <div className="mt-0.5 text-[10px] text-[var(--color-error)]">
                    {c.marks.map((m) => m.label).join(' / ')}
                  </div>
                )}
                {isOpen && (
                  <ul className="mt-1 flex flex-col gap-0.5 border-[var(--color-border)] border-t pt-1">
                    {c.strokes.length === 0 && (
                      <li className="text-[11px] text-[var(--color-text-muted)]">
                        ストロークはありません
                      </li>
                    )}
                    {c.strokes.slice(0, limit).map((s) => (
                      <li key={s.sid}>
                        <label className="flex items-center gap-1.5 text-[11px]">
                          <input
                            type="checkbox"
                            checked={isOut || sids.has(s.sid)}
                            disabled={isOut}
                            onChange={() => toggleSid(s.sid)}
                            aria-label={`${penLabel(s)} 太さ${s.width} 長さ${Math.round(s.len)}px を外す`}
                          />
                          <span className="text-[var(--color-text)]">
                            {penLabel(s)} 太さ{s.width}
                          </span>
                          <span className="text-[var(--color-text-muted)]">
                            長さ {Math.round(s.len)}px / 大きさ {formatAmount(s.ink)} /{' '}
                            {formatTimelapseTimeLabel(s.ts)}
                          </span>
                        </label>
                      </li>
                    ))}
                    {c.strokes.length > limit && (
                      <li>
                        <button
                          type="button"
                          onClick={() =>
                            setStrokeLimit((prev) => new Map(prev).set(c.uid, limit + STROKE_PAGE))
                          }
                          className="text-[11px] text-[var(--color-accent)] underline"
                        >
                          さらに {c.strokes.length - limit} 本を表示
                        </button>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            );
          })}
          {log.truncated && (
            <li className="p-1">
              <button
                type="button"
                data-testid="exclusion-read-earlier"
                onClick={readEarlier}
                disabled={settling !== null}
                className="w-full rounded border border-[var(--color-accent)] px-3 py-1.5 font-bold text-[var(--color-accent)] text-xs hover:bg-[var(--color-bg-soft)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                ▼ もっと前を読む (さらに 2 時間ぶん)
              </button>
            </li>
          )}
        </ul>
      </div>

      {/* 確定時の土台 (G) と、確定で失敗する条件 */}
      {verdict !== null && (
        <p
          data-testid="exclusion-base-note"
          role={verdictBlocks ? 'alert' : 'status'}
          className={`text-xs ${verdictBlocks ? 'text-[var(--color-error)]' : 'text-[var(--color-text-muted)]'}`}
        >
          {exclusionBaseMessage(verdict)}
        </p>
      )}

      {/* 確定 */}
      {job.t === 'error' && (
        <p role="alert" className="text-xs text-[var(--color-error)]">
          {job.message}
        </p>
      )}
      {job.t === 'running' && (
        <p role="status" className="flex items-center gap-2 text-[var(--color-text)] text-xs">
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
          />
          {exclusionPhaseLabel(job.phase)}
        </p>
      )}
      {restoring && (
        <p role="status" className="flex items-center gap-2 text-[var(--color-text)] text-xs">
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
          />
          復元しています…
        </p>
      )}
      {job.t === 'skipped-warning' && (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded border border-[#b8860b] p-2 text-xs"
        >
          <p className="text-[var(--color-text)]">
            {job.skipped}{' '}
            件の貼り付け画像を再現できませんでした。このまま戻すと、その画像は抜けた状態になります
            (外した版は過去キャンバスに保存されています)。
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setJob({ t: 'idle' })}
              className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1.5 text-white text-xs hover:bg-[var(--color-bg)]"
            >
              戻さない
            </button>
            <button
              type="button"
              onClick={() => {
                const id = job.pastCanvasId;
                setJob({ t: 'idle' });
                onBaked(id);
              }}
              className="rounded bg-[var(--color-error)] px-3 py-1.5 font-bold text-white text-xs hover:brightness-110"
            >
              このまま戻す
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className="mr-auto text-[11px] text-[var(--color-text-muted)]">
          {hasExclusion(selection) ? summaryLabel : '外す人・線を選んでください'}
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={job.t === 'running'}
          className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1.5 text-white text-xs hover:bg-[var(--color-bg)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          キャンセル
        </button>
        {armed ? (
          <>
            <button
              type="button"
              onClick={() => setArmed(false)}
              className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1.5 text-white text-xs hover:bg-[var(--color-bg)]"
            >
              やめる
            </button>
            <button
              type="button"
              onClick={() => startBake(log)}
              disabled={!canArm}
              className="rounded bg-[var(--color-error)] px-3 py-1.5 font-bold text-white text-xs hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
            >
              本当に戻す
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setArmed(true)}
            disabled={!canArm}
            title={
              hasExclusion(selection) && !previewSettled
                ? 'プレビューの更新が終わるまでお待ちください'
                : undefined
            }
            className="rounded bg-[var(--color-accent)] px-3 py-1.5 font-bold text-[var(--color-panel)] text-xs hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            外して戻す
          </button>
        )}
      </div>
    </div>
  );
};
